import { CardTransactionTypeEnum, Expense, Transaction, TransactionTypeEnum, WeavrAuthorizationTypesEnum } from "@finway-group/shared/lib/models"
import { TransactionStatusEnum } from "@finway-group/shared/lib/models/transaction/transactionStatus.enum"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { TransactionService } from "~Shared/services"
import { store } from "~Shared/store"
import { refetchTable } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { RootState } from "~Shared/store/rootState.interface"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"

export const useTransaction = () => useSelector(({ transactions }: RootState) => transactions.item)

export const useActiveTransactionTab = () => useSelector(({ transactions }: RootState) => transactions.activeTransactionTab)

const isAuthorizationRequest = (cardTransactionType: CardTransactionTypeEnum) =>
    ([WeavrAuthorizationTypesEnum.AUTHORISED, WeavrAuthorizationTypesEnum.AUTHORISED_CREDIT] as Array<CardTransactionTypeEnum>).includes(cardTransactionType)

const filterTransactionsToDisplay = (transactions: Array<ExpenseMatchedTransaction>, shouldFilterTransfers: boolean) =>
    transactions.filter(({ transactionType, cardData, status, transactionData }) => {
        // if not a card transaction/transfer we don't need to filter anything else
        if (transactionType != TransactionTypeEnum.CARD_TRANSACTION && transactionType !== TransactionTypeEnum.INTERNAL_TRANSFER) {
            return true
        }
        // only show transfers if indicated via flag
        if (shouldFilterTransfers && transactionType === TransactionTypeEnum.INTERNAL_TRANSFER) {
            return false
        }
        // only show pending authorization requests
        if (isAuthorizationRequest(cardData?.cardTransactionType) && status !== TransactionStatusEnum.PENDING) {
            return false
        }
        // only show partial reversals as long as the initial auth request is still pending
        if (cardData?.cardTransactionType === WeavrAuthorizationTypesEnum.ONLINE_REVERSE) {
            return (
                status === TransactionStatusEnum.COMPLETED &&
                transactions.find((txn: any) => cardData?.relatedTransactionIds.includes(txn.transactionId) && txn.status === TransactionStatusEnum.PENDING)
            )
        }
        // filter out 0 amounts
        if (!transactionData || transactionData.amount === 0) {
            return false
        }
        return true
    })

/**
 * API does not map _id to id for embedded transactions
 */
export type ExpenseMatchedTransaction = Omit<Transaction, "id"> & { _id: string }

export const useExpenseMatchedTransaction = (expense: Expense) => {
    const [matchedTransactions, setMatchedTransactions] = useStateIfMounted<Array<ExpenseMatchedTransaction>>([])

    const transactionsToDisplay = filterTransactionsToDisplay(matchedTransactions, true)

    useEffect(() => {
        if (expense.matchedTransactions.length === 0) {
            setMatchedTransactions([])
            return
        }

        // expense.matchedTransactions should be populated
        if (typeof expense.matchedTransactions[0] === "object") {
            setMatchedTransactions(expense.matchedTransactions as unknown as Array<ExpenseMatchedTransaction>)
        }
    }, [expense.id, JSON.stringify(expense.matchedTransactions)])

    return { matchedTransactions, transactionsToDisplay }
}

export const handleArchiveTransactions = async (transactionIds: Array<string>, table: TablesEnum) => {
    await TransactionService.archiveTransactions(transactionIds)
    store.dispatch(refetchTable(table))
    store.dispatch(refetchTable(TablesEnum.TRANSACTIONS_ARCHIVE))
}

export const handleUnarchiveTransactions = async (transactionIds: Array<string>) => {
    await TransactionService.unarchiveTransactions(transactionIds)

    store.dispatch(refetchTable(TablesEnum.TRANSACTIONS_INFLOW))
    store.dispatch(refetchTable(TablesEnum.TRANSACTIONS_OUTFLOW))
    store.dispatch(refetchTable(TablesEnum.TRANSACTIONS_ALL))
    store.dispatch(refetchTable(TablesEnum.TRANSACTIONS_ARCHIVE))
}
